import { jsx, jsxs } from "react/jsx-runtime";
import { useState, useEffect, Suspense } from "react";
import { ReCaptchaProvider } from "react-grecaptcha-v3";
import { connect, useSelector } from "react-redux";
import { useNavigate } from "react-router";
import { Outlet } from "react-router-dom";
import { cookieAcceptedName } from "@nxplatform/helpers/constants";
import { MobileBottomMenuView } from "@nxplatform/helpers/entities";
import { lazyImport } from "@nxplatform/helpers/helpers";
import { useWebView } from "@nxplatform/spa-service/hooks";
import { selectNotificationState } from "@service/api-client/slices";
import { ErrorBoundary } from "./common/error-boundary/error-boundary.js";
import { UISnackbarNotifications } from "./common/snackbar-notifications/ui-snackbar-notifications.js";
import { useAppSetup } from "./facades/route-switcher/app-setup.facade.js";
import { useCustomerData } from "./facades/route-switcher/customer-data.facade.js";
import { usePasswordRestore } from "./facades/route-switcher/password-restore.facade.js";
import { useSportsData } from "./facades/route-switcher/sports-data.facade.js";
import { useUserLimitToken } from "./facades/route-switcher/user-limit-token.facade.js";
import { useWsConnection } from "./facades/route-switcher/ws-connection.facade.js";
import { useAuthCustomerModuleProvider } from "./hooks/auth-customer-module-provider.js";
import { useMenuCmsHeaderHook } from "./hooks/menu-cms-header.hook.js";
import { useQueryModalOpenerHook } from "./hooks/query-modal-opener.hook.js";
import { useRenderCurrencyVariableHook } from "./hooks/render-currency-variable.hook.js";
import { ScrollToTop } from "./hooks/scroll-to-top.hook.js";
import { useTextPageFlagHook } from "./hooks/text-page-flag.hook.js";
import { useVerificationPageHook } from "./hooks/verification-page.hook.js";
import { RemoveTrailingSlash } from "./remove-trailing-slash.js";
import { RootLazyHooks as _RootLazyHooks } from "./root-lazy.hooks.js";
import { routeSwitcherConnect } from "./route-switcher.connect.js";
import { BaseLangRedirect } from "./routes/base-lang-redirect.js";
const { NecctonNotificationModal } = lazyImport(() => import("./modals/neccton-notification-modal.js"), "NecctonNotificationModal");
const { PromoSnackbar } = lazyImport(() => import("@nxplatform/platform-common/elements/promo-snackbar/promo-snackbar"), "PromoSnackbar");
const { UINotificationModal } = lazyImport(() => import("./modals/ui-notification-modal.js"), "UINotificationModal");
const Root = ({
  auth,
  loaded: loadedState,
  setup,
  clearState,
  customer,
  customerSocket,
  getCustomerInfo,
  getPromotionsList,
  confirmationSuccess,
  tokenLogin,
  changeBonusType,
  disabledPromotions,
  setRegistrationSuccess,
  checkRestorePasswordToken,
  tokenLoaded,
  tokenError,
  isTextPage,
  openModal,
  checkLimitToken,
  limitTokenSuccess,
  wsEndpoint,
  wsActive,
  promoOfferPopupEnabled,
  newTopMenu,
  isMenuCms,
  menuMainCmsHeaderEnabled,
  menuMainCmsTopEnabled,
  mobileBottomMenu,
  clearIsTextPageFlag,
  markUserActivity,
  currencyVariables,
  customerMainCurrency,
  customerIpData,
  baseCurrencyCode,
  recaptchaKey,
  getMenu,
  addPublicWsChannels,
  removePublicWsChannels,
  publicWsAliveChannels,
  Container,
  showNecctonNotificationEnabled,
  pageWithoutPromoSnackbar,
  country,
  fullSettingsLoaded
}) => {
  const authCustomerModuleLoaded = useAuthCustomerModuleProvider();
  const [appHooksReady, setAppHooksReady] = useState(false);
  const { id } = customer || {};
  const { enabled: mobileBottomMenuEnabled, view: mobileBottomMenuView } = mobileBottomMenu || {};
  const loaded = loadedState && (auth ? authCustomerModuleLoaded : true);
  const notificationModalVisible = useSelector(selectNotificationState);
  useWebView();
  const { appReady } = useAppSetup({
    loaded,
    setup
  });
  useEffect(() => {
    if (!appReady) return;
    const requestId = window.requestIdleCallback ? requestIdleCallback(() => setAppHooksReady(true), { timeout: 500 }) : setTimeout(() => setAppHooksReady(true), 100);
    return () => {
      if (window.cancelIdleCallback && typeof id === "number") {
        cancelIdleCallback(requestId);
      } else {
        clearTimeout(requestId);
      }
    };
  }, [appReady]);
  useUserLimitToken({ limitTokenSuccess, checkLimitToken });
  usePasswordRestore({ tokenError, tokenLoaded, checkRestorePasswordToken });
  useCustomerData({
    customer,
    wsActive,
    loaded,
    disabledPromotions,
    confirmationSuccess,
    changeBonusType,
    getCustomerInfo,
    getPromotionsList,
    customerSocket,
    tokenLogin,
    setRegistrationSuccess,
    markUserActivity
  });
  useSportsData({
    isTextPage,
    clearState,
    addPublicWsChannels,
    removePublicWsChannels,
    publicWsAliveChannels
  });
  useWsConnection({
    loaded,
    wsActive,
    wsEndpoint,
    publicWsAliveChannels,
    fullSettingsLoaded
  });
  useQueryModalOpenerHook({ openModal });
  useTextPageFlagHook({
    isTextPage,
    clearIsTextPageFlag
  });
  const navigate = useNavigate();
  useEffect(() => {
    window.$router = navigate;
  }, []);
  useVerificationPageHook();
  useRenderCurrencyVariableHook({
    currencyVariables,
    customerMainCurrency,
    auth,
    customerIpData,
    baseCurrencyCode
  });
  useMenuCmsHeaderHook({
    isMenuCms,
    newTopMenu,
    menuMainCmsHeaderEnabled,
    menuMainCmsTopEnabled,
    menuMainCmsBottomEnabled: mobileBottomMenuEnabled && mobileBottomMenuView === MobileBottomMenuView.cms,
    country,
    getMenu
  });
  return /* @__PURE__ */ jsx(ErrorBoundary, { children: /* @__PURE__ */ jsxs(ReCaptchaProvider, { scriptProps: { async: true, defer: true }, siteKey: recaptchaKey, injectionDelay: 9500, children: [
    /* @__PURE__ */ jsx(RemoveTrailingSlash, {}),
    /* @__PURE__ */ jsx(ScrollToTop, {}),
    /* @__PURE__ */ jsxs(BaseLangRedirect, { children: [
      loaded && /* @__PURE__ */ jsx(Container, { children: /* @__PURE__ */ jsx(Outlet, {}) }),
      appHooksReady && promoOfferPopupEnabled && !pageWithoutPromoSnackbar && /* @__PURE__ */ jsx(Suspense, { children: /* @__PURE__ */ jsx(PromoSnackbar, { cookieName: cookieAcceptedName }) }),
      appHooksReady && /* @__PURE__ */ jsx(_RootLazyHooks, {})
    ] }),
    /* @__PURE__ */ jsxs(Suspense, { children: [
      notificationModalVisible && /* @__PURE__ */ jsx(UINotificationModal, {}),
      showNecctonNotificationEnabled && /* @__PURE__ */ jsx(NecctonNotificationModal, {})
    ] }),
    /* @__PURE__ */ jsx(UISnackbarNotifications, {})
  ] }) });
};
const _Root = connect(
  routeSwitcherConnect.mapStateToProps,
  routeSwitcherConnect.mapDispatchToProps
)(Root);
export {
  _Root as Root,
  _Root
};
